import React from 'react';
import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes.ts';
import {
  Activity,
  ArrowRight,
  Bell,
  Check,
  CreditCard,
  type LucideProps,
  Search,
  ShieldCheck,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';

type IconComponent = React.ComponentType<LucideProps>;

export default function CommentCaMarche() {
  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans selection:bg-blue-100'>
      <PageMeta
        title='Comment ça marche'
        description='Découvrez comment BoulesConnect fonctionne en 3 étapes : trouvez un concours de pétanque, inscrivez votre équipe en ligne et payez en quelques clics.'
      />
      {/* --- HERO SECTION --- */}
      <section className='pt-32 pb-20 px-6 bg-gradient-to-br from-blue-50 via-white to-emerald-50 overflow-hidden'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center mb-16'>
            <div className='inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-bold mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700'>
              <Zap className='w-4 h-4' />
              <span>Inscription en 3 minutes chrono</span>
            </div>

            <h1 className='text-5xl md:text-6xl font-black tracking-tight text-slate-900 mb-6 leading-tight'>
              Participer à un concours
              <br />
              n'a jamais été aussi{' '}
              <span className='bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent'>
                simple
              </span>
            </h1>

            <p className='text-xl text-slate-600 mb-12 max-w-2xl mx-auto leading-relaxed'>
              Oubliez les inscriptions par téléphone ou sur papier. Avec BoulesConnect, tout se fait en quelques clics.
            </p>

            <div className='flex flex-wrap items-center justify-center gap-4'>
              <a
                href={`#steps`}
                className='bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full text-lg font-bold shadow-xl shadow-blue-200 transition-all active:scale-95'
              >
                Découvrir les étapes
              </a>

              <Link
                to={ROUTES.SEARCH_CONCOURS}
                className='bg-white hover:bg-slate-50 text-slate-900 px-8 py-4 rounded-full text-lg font-bold border-2 border-slate-200 transition-all'
              >
                Voir les concours
              </Link>
            </div>
          </div>

          {/* Illustration centrale (Animation flottante via Tailwind) */}
          <div className='relative max-w-5xl mx-auto animate-bounce-slow'>
            <div className='bg-white rounded-3xl shadow-2xl border-2 border-slate-200 p-8'>
              <div className='flex flex-col md:flex-row items-center justify-between gap-8'>
                <div className='flex-1 w-full space-y-4'>
                  <ProgressStep number={1} title='Trouvez votre concours' color='blue' width='w-full' />
                  <ProgressStep number={2} title='Inscrivez votre équipe' color='emerald' width='w-3/4' />
                  <ProgressStep number={3} title='Payez en ligne' color='purple' width='w-1/2' />
                </div>

                <div className='w-64 h-64 bg-gradient-to-br from-blue-100 to-emerald-100 rounded-3xl flex items-center justify-center'>
                  <Check className='w-32 h-32 text-emerald-600' />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- LES 3 ÉTAPES --- */}
      <section id='steps' className='py-24 px-6'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center mb-20'>
            <h2 className='text-5xl font-black text-slate-900 mb-4'>Comment ça marche ?</h2>
            <p className='text-xl text-slate-600 max-w-2xl mx-auto'>
              3 étapes simples pour participer à votre prochain concours
            </p>
          </div>

          <div className='space-y-32'>
            {/* Étape 1 */}
            <div className='grid md:grid-cols-2 gap-16 items-center'>
              <div>
                <StepBadge number={1} label='Étape 1' color='blue' />
                <h3 className='text-4xl font-black text-slate-900 mb-6'>Trouvez le concours parfait</h3>
                <p className='text-lg text-slate-600 mb-8 leading-relaxed'>
                  Parcourez des centaines de concours partout en France. Filtrez par date, distance, type de jeu et
                  niveau pour trouver celui qui vous correspond.
                </p>
                <ul className='space-y-4'>
                  <StepFeature text='Recherche par localisation et distance' />
                  <StepFeature text='Filtres SINGLE, DOUBLE ou TRIPLE' />
                  <StepFeature text='Voir les places disponibles en temps réel' />
                  <StepFeature text='Détails complets : dotation, horaires, lieu' />
                </ul>
                <Link
                  to={ROUTES.SEARCH_CONCOURS}
                  className='inline-flex items-center gap-2 mt-8 text-blue-600 hover:text-blue-700 font-bold transition-colors'
                >
                  Explorer les concours <ArrowRight className='w-5 h-5' />
                </Link>
              </div>
              <div className='bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-8 text-white shadow-2xl transform hover:rotate-2 transition-transform'>
                <div className='bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-4 border border-white/20'>
                  <div className='flex items-center gap-3 mb-4'>
                    <Search className='w-6 h-6' />
                    <span className='font-bold'>Rechercher un concours</span>
                  </div>
                  <div className='bg-white rounded-xl p-3 text-slate-900 mb-3 shadow-inner'>
                    <p className='text-sm font-semibold'>Lyon, ce week-end</p>
                  </div>
                  <div className='flex gap-2'>
                    <span className='px-3 py-1 bg-white/20 rounded-lg text-xs font-semibold'>DOUBLE</span>
                    <span className='px-3 py-1 bg-white/20 rounded-lg text-xs font-semibold'>25 km</span>
                  </div>
                </div>
                <div className='space-y-3'>
                  <div className='bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10'>
                    <p className='font-bold'>Concours National de Lyon</p>
                    <p className='text-xs text-blue-100'>48 places disponibles • 30€</p>
                  </div>
                  <div className='bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10 opacity-60'>
                    <p className='font-bold'>Tournoi des Champions</p>
                    <p className='text-xs text-blue-100'>16 places disponibles • 50€</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Étape 2 */}
            <div className='grid md:grid-cols-2 gap-16 items-center'>
              <div className='order-2 md:order-1'>
                <div className='bg-gradient-to-br from-emerald-600 to-teal-600 rounded-3xl p-8 text-white shadow-2xl transform hover:-rotate-2 transition-transform'>
                  <div className='bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6 border border-white/20'>
                    <div className='flex items-center gap-3 mb-6'>
                      <Users className='w-6 h-6' />
                      <span className='font-bold'>Votre équipe</span>
                    </div>
                    <div className='space-y-4'>
                      <div className='bg-white rounded-xl p-3 text-slate-900 shadow-inner'>
                        <p className='text-[10px] text-slate-400 uppercase font-bold'>Joueur 1</p>
                        <p className='font-bold'>Pierre Dubois</p>
                      </div>
                      <div className='bg-white rounded-xl p-3 text-slate-900 shadow-inner'>
                        <p className='text-[10px] text-slate-400 uppercase font-bold'>Joueur 2</p>
                        <p className='font-bold'>Sophie Bernard</p>
                      </div>
                    </div>
                  </div>
                  <div className='bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10 flex items-center justify-between'>
                    <span className='text-sm opacity-80'>Total inscription</span>
                    <span className='text-2xl font-black'>30,00 €</span>
                  </div>
                </div>
              </div>
              <div className='order-1 md:order-2'>
                <StepBadge number={2} label='Étape 2' color='emerald' />
                <h3 className='text-4xl font-black text-slate-900 mb-6'>Inscrivez votre équipe</h3>
                <p className='text-lg text-slate-600 mb-8 leading-relaxed'>
                  Remplissez un formulaire simple avec les informations de votre équipe. Tout est pré-rempli si vous
                  avez déjà un compte.
                </p>
                <ul className='space-y-4'>
                  <StepFeature text='Formulaire rapide et intuitif' />
                  <StepFeature text='Vos données sauvegardées pour la prochaine fois' />
                  <StepFeature text='Ajout de partenaires en un clic' />
                  <StepFeature text='Options supplémentaires (repas, photos...)' />
                </ul>
              </div>
            </div>

            {/* Étape 3 */}
            <div className='grid md:grid-cols-2 gap-16 items-center'>
              <div>
                <StepBadge number={3} label='Étape 3' color='purple' />
                <h3 className='text-4xl font-black text-slate-900 mb-6'>Payez en toute sécurité</h3>
                <p className='text-lg text-slate-600 mb-8 leading-relaxed'>
                  Réglez votre inscription en ligne par carte bancaire. Paiement 100% sécurisé et confirmation
                  instantanée par email.
                </p>
                <ul className='space-y-4'>
                  <StepFeature text='Paiement sécurisé par Stripe' />
                  <StepFeature text='Confirmation instantanée par email' />
                  <StepFeature text='Reçus automatiques' />
                  <StepFeature text='Notifications par email' />
                </ul>
              </div>
              <div className='bg-gradient-to-br from-purple-600 to-indigo-600 rounded-3xl p-8 text-white shadow-2xl transform hover:rotate-2 transition-transform'>
                <div className='bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6 border border-white/20'>
                  <div className='flex items-center gap-3 mb-6'>
                    <CreditCard className='w-6 h-6' />
                    <span className='font-bold'>Paiement sécurisé</span>
                  </div>
                  <div className='bg-white rounded-xl p-4 text-slate-900 mb-4 shadow-inner'>
                    <p className='text-[10px] text-slate-400 uppercase font-bold mb-1'>Numéro de carte</p>
                    <p className='font-bold tracking-widest'>•••• •••• •••• 4242</p>
                  </div>
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='bg-white rounded-xl p-3 text-slate-900 shadow-inner'>
                      <p className='text-[10px] text-slate-400 uppercase font-bold'>Expiration</p>
                      <p className='font-bold'>12/26</p>
                    </div>
                    <div className='bg-white rounded-xl p-3 text-slate-900 shadow-inner'>
                      <p className='text-[10px] text-slate-400 uppercase font-bold'>CVC</p>
                      <p className='font-bold'>•••</p>
                    </div>
                  </div>
                </div>
                {/* Bouton démo illustratif — pas de handler attendu */}
                <button className='w-full bg-white text-purple-600 py-4 rounded-xl font-black shadow-lg hover:scale-[1.02] transition-transform'>
                  Payer 30,00 €
                </button>
                <div className='flex items-center justify-center gap-2 mt-6 text-xs text-purple-100 opacity-80'>
                  <ShieldCheck className='w-4 h-4' />
                  <span>Paiement crypté SSL et sécurisé par Stripe</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- ET APRÈS ? --- */}
      <section className='py-24 px-6 bg-slate-50 border-y border-slate-200'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl font-black text-slate-900 mb-4'>Et après l'inscription ?</h2>
            <p className='text-xl text-slate-600'>Profitez de tous les avantages de BoulesConnect</p>
          </div>

          <div className='grid md:grid-cols-3 gap-8'>
            <PostInscCard
              Icon={Bell}
              title='Notifications'
              desc="Confirmation d'inscription, rappels automatiques J-7 et J-1, suivi de paiement et notifications importantes directement dans votre espace joueur."
              color='blue'
            />
            <PostInscCard
              Icon={Activity}
              title='Votre historique'
              desc='Retrouvez toutes vos participations, résultats et classements directement dans votre profil joueur.'
              color='emerald'
            />
            <PostInscCard
              Icon={Trophy}
              title='Votre palmarès'
              desc='Toutes vos participations et victoires sont enregistrées automatiquement dans votre profil.'
              color='purple'
            />
          </div>
        </div>
      </section>

      {/* --- STATS --- */}
      <section className='py-20 bg-blue-600 text-white px-6'>
        <div className='max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-12 text-center'>
          <StatItem value='15 000+' label='Joueurs inscrits *' />
          <StatItem value='1 200+' label='Concours organisés *' />
          <StatItem value='1 400+' label='Clubs dans notre annuaire' />
          <StatItem value='FFPJP' label='Licences supportées' />
        </div>
        <p className='text-xs text-blue-200 text-center mt-6'>
          * Volumes cumulés depuis le lancement de la V1 sur Quare Systems (2021) et de BoulesConnect.
        </p>
      </section>

      {/* --- CTA FINAL --- */}
      <section className='py-24 px-6'>
        <div className='max-w-4xl mx-auto text-center bg-white rounded-[3rem] p-12 md:p-20 shadow-2xl shadow-blue-100 border border-slate-100'>
          <h2 className='text-5xl font-black text-slate-900 mb-6'>Prêt à commencer ?</h2>
          <p className='text-xl text-slate-600 mb-10'>
            Rejoignez les joueurs qui simplifient leurs inscriptions avec BoulesConnect
          </p>
          <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link
              to={ROUTES.SEARCH_CONCOURS}
              className='bg-blue-600 hover:bg-blue-700 text-white px-10 py-5 rounded-full text-lg font-bold shadow-xl shadow-blue-200 transition-all active:scale-95 w-full sm:w-auto'
            >
              Trouver un concours
            </Link>
            <Link
              to={ROUTES.TARIFS}
              className='bg-slate-100 hover:bg-slate-200 text-slate-900 px-10 py-5 rounded-full text-lg font-bold transition-all w-full sm:w-auto'
            >
              Voir les tarifs
            </Link>
          </div>
          <p className='text-sm text-slate-400 mt-8'>Gratuit pour les joueurs • Inscription immédiate</p>
        </div>
      </section>
    </div>
  );
}

// --- SOUS-COMPOSANTS ---

function ProgressStep({
  number,
  title,
  color,
  width,
}: {
  number: number;
  title: string;
  color: 'blue' | 'emerald' | 'purple';
  width: string;
}) {
  const themes = {
    blue: { bg: 'bg-blue-50', dot: 'bg-blue-600', bar: 'bg-blue-600' },
    emerald: { bg: 'bg-emerald-50', dot: 'bg-emerald-600', bar: 'bg-emerald-600' },
    purple: { bg: 'bg-purple-50', dot: 'bg-purple-600', bar: 'bg-purple-600' },
  };
  const theme = themes[color];

  return (
    <div className={`${theme.bg} rounded-2xl p-4 transition-transform hover:scale-[1.02]`}>
      <div className='flex items-center gap-3 mb-2'>
        <div
          className={`w-8 h-8 ${theme.dot} rounded-full flex items-center justify-center text-white font-bold text-sm`}
        >
          {number}
        </div>
        <p className='font-bold text-slate-900'>{title}</p>
      </div>
      <div className='h-2 bg-slate-200 rounded-full overflow-hidden'>
        <div className={`h-full ${width} ${theme.bar} rounded-full`}></div>
      </div>
    </div>
  );
}

function StepBadge({ number, label, color }: { number: number; label: string; color: 'blue' | 'emerald' | 'purple' }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-700 dot:bg-blue-600',
    emerald: 'bg-emerald-100 text-emerald-700 dot:bg-emerald-600',
    purple: 'bg-purple-100 text-purple-700 dot:bg-purple-600',
  };

  return (
    <div
      className={`inline-flex items-center gap-3 px-4 py-2 ${colors[color].split(' dot:')[0]} rounded-full font-bold mb-8 shadow-sm`}
    >
      <div
        className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm ${color === 'blue' ? 'bg-blue-600' : color === 'emerald' ? 'bg-emerald-600' : 'bg-purple-600'}`}
      >
        {number}
      </div>
      <span>{label}</span>
    </div>
  );
}

function StepFeature({ text }: { text: string }) {
  return (
    <li className='flex items-center gap-3 text-slate-700 group'>
      <div className='w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-500 transition-colors'>
        <Check className='w-4 h-4 text-emerald-600 group-hover:text-white transition-colors' />
      </div>
      <span className='font-medium'>{text}</span>
    </li>
  );
}

function PostInscCard({
  Icon,
  title,
  desc,
  color,
}: {
  Icon: IconComponent;
  title: string;
  desc: string;
  color: 'blue' | 'emerald' | 'purple';
}) {
  const themes = {
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  return (
    <div className='bg-white rounded-[2rem] p-10 border border-slate-200 shadow-xl shadow-slate-200/50 hover:-translate-y-2 transition-all duration-300'>
      <div className={`w-16 h-16 ${themes[color]} rounded-2xl flex items-center justify-center mb-8`}>
        <Icon className='w-8 h-8' />
      </div>
      <h3 className='text-2xl font-black text-slate-900 mb-4'>{title}</h3>
      <p className='text-slate-600 leading-relaxed'>{desc}</p>
    </div>
  );
}

function StatItem({ value, label }: { value: string; label: string }) {
  return (
    <div className='space-y-2'>
      <p className='text-5xl font-black'>{value}</p>
      <p className='text-blue-100 font-medium uppercase tracking-widest text-xs'>{label}</p>
    </div>
  );
}
